import { Field, FieldValidator, useFormikContext } from "formik";
import React, { forwardRef } from "react";
import { Experience } from "types";
import shallow from "zustand/shallow";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { PaymentInputRef, TokenizePaymentParams } from "../Input/PaymentInputRef";
import { CheckoutFormValues } from "./CheckoutPurchaseScreen.types";
import { CreatePurchasePaymentFields, CreatePurchasePaymentFieldsProps } from "./CreatePurchasePaymentFields";
import { CreatePurchasePaymentWorkflow } from "./CreatePurchasePaymentWorkflow";
import { isCreditCardRequired } from "@/models/Experience";

interface CreatePurchasePaymentDetailsProps {
    validate?: FieldValidator;

    onAdditionalDetailsSubmit: (
        tokenizePaymentParams: TokenizePaymentParams,
        values: CheckoutFormValues,
    ) => Promise<void>;

    onAdditionalDetailsSubmitSuccess?: () => void;
}

export const CreatePurchasePaymentDetails = forwardRef<PaymentInputRef, CreatePurchasePaymentDetailsProps>(
    ({ validate, onAdditionalDetailsSubmit, onAdditionalDetailsSubmitSuccess }, ref) => {
        const { values } = useFormikContext<CheckoutFormValues>();

        const [purchaseAmount, purchaseBalance, isSplitPay] = useCreatePurchaseStore(
            (state) => [state.amount, state.balance, state.purchase.splitPayment?.enabled],
            shallow,
        );

        const isCreditCardRequest = useCreatePurchaseStore((state) =>
            isCreditCardRequired(state.getActiveProduct<Experience>()),
        );

        const isBalanceDueZero = purchaseBalance === 0;
        const shouldShowPaymentMethods = (purchaseAmount === 0 && isCreditCardRequest) || !isBalanceDueZero;

        if (!shouldShowPaymentMethods) {
            return null;
        }

        const handleAdditionalDetailsSubmit: CreatePurchasePaymentFieldsProps["onAdditionalDetailsSubmit"] = async (
            data,
        ) => onAdditionalDetailsSubmit(data, values);

        return (
            <>
                <h3 className="mt-4 mb-2">Payment</h3>
                <div className="space-y-4">
                    <CreatePurchasePaymentWorkflow />
                    {!isSplitPay && (
                        <Field name="payment" validate={validate}>
                            {() => (
                                <CreatePurchasePaymentFields
                                    ref={ref}
                                    onAdditionalDetailsSubmit={handleAdditionalDetailsSubmit}
                                    onAdditionalDetailsSubmitSuccess={onAdditionalDetailsSubmitSuccess}
                                />
                            )}
                        </Field>
                    )}
                </div>
            </>
        );
    },
);

CreatePurchasePaymentDetails.displayName = "CreatePurchasePaymentDetails";
